import { useCallback, useState } from 'react';
import { GridHeaderSelectionChangeEvent, GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import { DOC_ID_FIELD } from '@cs-cms/types';
import { useDocumentsContext } from '@cs-cms/state-management';
import { FilterActionsHandle } from '../toolbar/filterActions';
import { CpsGridDocument } from '../cpsGridTypes';

interface UseCpsGridSelectionParams {
  gridData: CpsGridDocument[];
  selectedIndexId: number | undefined;
  selectedView: number | undefined;
}

interface UseCpsGridSelectionResult {
  select: SelectDescriptor | undefined;
  onFilterActionsMount: (actions: FilterActionsHandle) => void;
  onSelectionChange: (event: GridSelectionChangeEvent) => void;
  onHeaderSelectionChange: (event: GridHeaderSelectionChangeEvent) => void;
  handleContextMenu: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export const useCpsGridSelection = (params: UseCpsGridSelectionParams): UseCpsGridSelectionResult => {
  const { gridData, selectedIndexId, selectedView } = params;
  const { setSelectedDocuments } = useDocumentsContext();
  const [selectionByView, setSelectionByView] = useState<Record<string, SelectDescriptor>>({});
  const [filterActionsHandle, setFilterActionsHandle] = useState<FilterActionsHandle | null>(null);

  const viewKey = `${selectedView ?? 'none'}-${selectedIndexId ?? 'none'}`;
  const select = selectionByView[viewKey] ?? {};

  const syncSelectedDocuments = (selection: SelectDescriptor, dataItems: CpsGridDocument[]): void => {
    if (!setSelectedDocuments) {
      return;
    }

    const selectedIds = Object.keys(selection).filter(id => selection[id] === true);
    const selectedItems = dataItems.filter(item => selectedIds.includes(item[DOC_ID_FIELD]));
    setSelectedDocuments(selectedItems);
  };

  const onSelectionChange = (event: GridSelectionChangeEvent): void => {
    const selection = window.getSelection();
    const isShiftClick = event.nativeEvent?.shiftKey;
    if (isShiftClick) {
      selection?.removeAllRanges();
    }
    if (selection && selection.toString().length > 0) {
      return;
    }

    const target = event.nativeEvent?.target as HTMLElement | null;
    if (target?.closest('.linkCell, .linkedCell')) {
      return;
    }

    syncSelectedDocuments(event.select, event.dataItems);
    setSelectionByView(prev => ({
      ...prev,
      [viewKey]: event.select
    }));
  };

  const onHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent): void => {
    syncSelectedDocuments(event.select, event.dataItems);
    setSelectionByView(prev => ({
      ...prev,
      [viewKey]: event.select
    }));
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement;
    const row = target.closest('tr[data-grid-row-index]');

    if (!row || gridData.length === 0) {
      return;
    }

    e.preventDefault();

    const rowIndex = parseInt(row.getAttribute('data-grid-row-index') || '0', 10);
    const clickedDocument = gridData[rowIndex];
    if (!clickedDocument) {
      return;
    }

    const docId = clickedDocument[DOC_ID_FIELD];
    if (!select?.[docId]) {
      const newSelect = { ...select, [docId]: true };
      setSelectionByView(prev => ({
        ...prev,
        [viewKey]: newSelect
      }));
      syncSelectedDocuments(newSelect, gridData);
    }

    setTimeout(() => {
      filterActionsHandle?.openContextMenu(e.clientX, e.clientY);
    }, 0);
  };

  const onFilterActionsMount = useCallback((actions: FilterActionsHandle): void => {
    setFilterActionsHandle(actions);
  }, []);

  return {
    select,
    onFilterActionsMount,
    onSelectionChange,
    onHeaderSelectionChange,
    handleContextMenu
  };
};
